/**
 * Control record schema — DataTable columns + FormModal fields for
 * the Controls half of Step 8 (`cybersecurity_concept_agent`, per
 * ISO 21434 RQ-09-08). Controls implement Step 7 goals; each
 * requirement (the other half of Step 8) implements one control.
 *
 * Real DB shape:
 *   { control_id (PK), control_name, control_description, control_type,
 *     goal_id,
 *     // hierarchical: source, source_session_id, is_relevant,
 *     // relevance_justification, affected_components }
 */
import React from 'react';
import CategoryPill from '../../../../components/ui/CategoryPill';

export interface ControlRecord {
  control_id: string;
  control_name?: string;
  control_description?: string;
  control_type?: string;
  goal_id?: string;
  source?: string;
  source_session_id?: string;
  is_relevant?: boolean;
  relevance_justification?: string;
  [key: string]: unknown;
}

const renderType = (row: ControlRecord) =>
  row.control_type ? <CategoryPill label={String(row.control_type)} size="sm" /> : '';

export const CONTROL_COLUMNS_READ = [
  { key: 'control_id', label: 'ID', sortable: true, width: '8%' },
  { key: 'control_name', label: 'Name', sortable: true, width: '20%' },
  { key: 'control_description', label: 'Description', sortable: false, width: '40%' },
  { key: 'control_type', label: 'Type', sortable: true, width: '17%', render: renderType },
  { key: 'goal_id', label: 'Parent Goal', sortable: true, width: '15%' },
];

export const CONTROL_COLUMNS_EDIT = [
  { key: 'control_id', label: 'ID', sortable: true, width: '7%' },
  { key: 'control_name', label: 'Name', sortable: true, width: '19%' },
  { key: 'control_description', label: 'Description', sortable: false, width: '38%' },
  { key: 'control_type', label: 'Type', sortable: true, width: '17%', render: renderType },
  { key: 'goal_id', label: 'Parent Goal', sortable: true, width: '15%' },
];

export const CONTROL_ACTIONS_COLUMN_WIDTH = '4%';

const CONTROL_ID_PATTERN = /^[A-Z0-9_-]+$/;

export const CONTROL_FORM_FIELDS = [
  {
    name: 'control_id',
    label: 'Control ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. CTRL-01',
    helpText: 'Unique identifier (per ISO 21434 RQ-09-08).',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Control ID is required';
      if (!CONTROL_ID_PATTERN.test(s)) {
        return 'Control ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'goal_id',
    label: 'Parent Goal',
    type: 'select',
    required: true,
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'The cybersecurity goal this control achieves.',
    width: 'half',
  },
  {
    name: 'control_name',
    label: 'Control Name',
    type: 'text',
    required: true,
    placeholder: 'e.g. Firmware Signature Verification',
  },
  {
    name: 'control_type',
    label: 'Control Type',
    type: 'text',
    required: true,
    placeholder: 'e.g. Cryptographic, Access Control, Secure Boot',
    helpText: 'Category of security control (free-text per ISO 21434).',
  },
  {
    name: 'control_description',
    label: 'Control Description',
    type: 'textarea',
    required: true,
    rows: 4,
    placeholder: 'Describe what the control does to achieve the goal.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 20) return 'Description must be at least 20 characters.';
      return null;
    },
  },
];
